import { useState } from "react";
import { LANGUAGE_LABELS } from "@templestay/i18n";
import type { Language } from "@templestay/i18n";

const LANGS: Language[] = ["ko", "en", "zh", "ja"];

type FieldDef = {
  key: string;
  label: string;
  multiline?: boolean;
  placeholder?: Record<Language, string>;
};

type I18nValue = Record<Language, Record<string, string>>;

type Props = {
  value: I18nValue;
  fields: FieldDef[];
  onChange: (value: I18nValue) => void;
  defaultLang?: Language;
  requiredFields?: string[];
  errors?: Partial<Record<Language, string>>;
};

function langCompletion(langData: Record<string, string>, fields: FieldDef[]): number {
  if (fields.length === 0) return 100;
  const filled = fields.filter((f) => (langData[f.key] ?? "").trim().length > 0).length;
  return Math.round((filled / fields.length) * 100);
}

export function I18nEditor({ value, fields, onChange, defaultLang, requiredFields = [], errors }: Props) {
  const [activeLang, setActiveLang] = useState<Language>("ko");

  function update(lang: Language, key: string, v: string) {
    onChange({
      ...value,
      [lang]: {
        ...(value[lang] ?? {}),
        [key]: v,
      },
    });
  }

  const current = value[activeLang] ?? {};

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Language tabs */}
      <div className="flex border-b border-gray-100">
        {LANGS.map((lang) => {
          const pct = langCompletion(value[lang] ?? {}, fields);
          const isDefault = lang === defaultLang;
          const hasError = !!errors?.[lang];
          return (
            <button
              key={lang}
              onClick={() => setActiveLang(lang)}
              type="button"
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                lang === activeLang
                  ? "border-b-2 border-brand-500 text-brand-600"
                  : hasError
                  ? "text-red-400 hover:text-red-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span>{LANGUAGE_LABELS[lang]}</span>
              {isDefault && <span className="ml-0.5 text-brand-500">*</span>}
              <span className={`ml-1 text-[10px] font-normal ${pct === 100 ? "text-green-500" : pct > 0 ? "text-amber-500" : "text-gray-400"}`}>
                {pct}%
              </span>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {errors?.[activeLang] && (
        <div className="bg-red-50 px-4 py-2 text-xs text-red-600">{errors[activeLang]}</div>
      )}

      {/* Fields */}
      <div className="space-y-3 p-4">
        {fields.map((field) => {
          const isRequired = requiredFields.includes(field.key) && activeLang === defaultLang;
          const isEmpty = !(current[field.key] ?? "").trim();
          const showError = isRequired && isEmpty;
          return field.multiline ? (
            <div key={field.key}>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                {field.label}{isRequired && <span className="ml-0.5 text-red-400">*</span>}
              </label>
              <textarea
                value={current[field.key] ?? ""}
                onChange={(e) => update(activeLang, field.key, e.target.value)}
                rows={3}
                placeholder={field.placeholder?.[activeLang]}
                className={`w-full resize-y rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 ${
                  showError ? "border-red-300 focus:border-red-400 focus:ring-red-50" : "border-gray-200 focus:border-brand-400 focus:ring-brand-50"
                }`}
              />
            </div>
          ) : (
            <div key={field.key}>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                {field.label}{isRequired && <span className="ml-0.5 text-red-400">*</span>}
              </label>
              <input
                type="text"
                value={current[field.key] ?? ""}
                onChange={(e) => update(activeLang, field.key, e.target.value)}
                placeholder={field.placeholder?.[activeLang]}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 ${
                  showError ? "border-red-300 focus:border-red-400 focus:ring-red-50" : "border-gray-200 focus:border-brand-400 focus:ring-brand-50"
                }`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const EMPTY_I18N: I18nValue = { ko: {}, en: {}, zh: {}, ja: {} };
